"use client";

// Updated Footer.jsx - Social links centered at bottom
import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { 
  FaFacebook, FaTwitter, FaInstagram, FaLinkedin,
  FaPhone, FaEnvelope, FaArrowUp,
  FaGraduationCap, FaBook, FaUsers, FaCalendar, FaPencilAlt
} from 'react-icons/fa';
import './Footer.css';

function Footer() {
  const [isScrollVisible, setIsScrollVisible] = useState(false);
  const footerRef = useRef(null);
  const isInView = useInView(footerRef, { once: true });

  useEffect(() => {
    const handleScroll = () => setIsScrollVisible(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Event & Workshops', path: '/workshops' },
    { name: 'Blogs', path: '/blogs' },
    { name: 'Newsletter', path: '/newsletter' },
    { name: 'Contact Us', path: '/contact' }
  ];

  // Services section
  const services = [
    { name: 'Admission Consulting', icon: <FaGraduationCap />, path: '/consultancy' },
    { name: 'Internship', icon: <FaBook />, path: '/internship' },
    { name: 'Research', icon: <FaUsers />, path: '/research' },
    { name: 'Essay Editing Services', icon: <FaCalendar />, path: '/essay' }
  ];

  // Exam preparations section
  const examPreparations = [
    { name: 'SAT Preparation', icon: <FaPencilAlt />, path: '/sat' },
    { name: 'ACT Preparation', icon: <FaPencilAlt />, path: '/act' },
    { name: 'GMAT Preparation', icon: <FaPencilAlt />, path: '/gmat' },
    { name: 'GRE Preparation', icon: <FaPencilAlt />, path: '/gre' }
  ];

  // Academics section
  const academics = [
    { name: 'AP Courses', icon: <FaPencilAlt />, path: '/ap' },
    { name: 'MYP', icon: <FaPencilAlt />, path: '/myp' },
    { name: 'AMC', icon: <FaPencilAlt />, path: '/amc' },
    { name: 'IGCSE', icon: <FaPencilAlt />, path: '/igcse' },
    { name: 'A LEVEL', icon: <FaPencilAlt />, path: '/alevel' },
    { name: 'IB', icon: <FaPencilAlt />, path: '/ib' }
  ];

  const socialLinks = [
    { name: 'Facebook', icon: <FaFacebook />, url: 'https://facebook.com' },
    { name: 'Twitter', icon: <FaTwitter />, url: 'https://twitter.com' },
    { name: 'Instagram', icon: <FaInstagram />, url: 'https://www.instagram.com/engagehub_llc/' },
    { name: 'LinkedIn', icon: <FaLinkedin />, url: 'https://linkedin.com' }
  ];

  return (
    <footer ref={footerRef} className="footer">
      <div className="footer-container">
        {/* Quick links section */}
        <motion.div 
          className="footer-section"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h3>Quick Links</h3>
          <ul className="footer-links">
            {quickLinks.map(({ name, path }) => (
              <li key={name}>
                <a href={path}>{name}</a>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Our Services section */}
        <motion.div 
          className="footer-section"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <h3>Our Services</h3>
          <div className="footer-info">
            {services.map(({ name, icon, path }) => (
              <a key={name} href={path} className="footer-item footer-exam-link">
                <span className="exam-icon-wrapper">{icon}</span>
                <span className="exam-text">{name}</span>
              </a>
            ))}
          </div>
        </motion.div>

        {/* Exam Preparations section */}
        <motion.div 
          className="footer-section"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h3>Exam Preparation</h3>
          <div className="footer-info">
            {examPreparations.map(({ name, icon, path }) => (
              <a 
                key={name}
                href={path}
                className="footer-item footer-exam-link"
              >
                <span className="exam-icon-wrapper">{icon}</span>
                <span className="exam-text">{name}</span>
              </a>
            ))}
          </div>
        </motion.div>

        {/* Academics section */}
        <motion.div 
          className="footer-section"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <h3>Academics</h3>
          <div className="footer-info">
            {academics.map(({ name, icon, path }) => (
              <a 
                key={name}
                href={path}
                className="footer-item footer-exam-link"
              >
                <span className="exam-icon-wrapper">{icon}</span>
                <span className="exam-text">{name}</span>
              </a>
            ))}
          </div>
        </motion.div>

        {/* Contact information section */}
        <motion.div 
          className="footer-section"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3>Contact Info</h3>
          <div className="contact-info">
            <div className="contact-item">
              <FaPhone className="contact-icon-phone" />
              <div>
                <span className="contact-label">Phone</span>
                <span className="contact-value">+971 55 272 8339</span>
              </div>
            </div>
            <div className="contact-item">
              <FaEnvelope className="contact-icon-email" />
              <div>
                <span className="contact-label">Email</span>
                <span className="contact-value">divya@engagehub.me</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Social Links - Centered in a row */}
      <motion.div 
        className="social-links-section"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.5 }}
      >
        <div className="social-links-grid">
          {socialLinks.map(({ name, icon, url }) => (
            <motion.a
              key={name}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="social-link"
              aria-label={name}
            >
              {icon}
            </motion.a>
          ))}
        </div>
      </motion.div>

      {/* Bottom Bar */}
      <div className="footer-bottom">
        <div className="footer-bottom-container">
          <div className="copyright">
            <p>&copy; 2025 EngageHub. All rights reserved.</p>
          </div>

          <AnimatePresence>
            {isScrollVisible && (
              <motion.button
                className="scroll-to-top"
                onClick={scrollToTop}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Scroll to top"
              >
                <FaArrowUp />
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>
    </footer>
  );
}

export default Footer;